import {
  DeleteOutlined as DeleteIcon,
  PreviewOutlined as PreviewIcon,
  FileUploadOutlined as UploadIcon,
  DescriptionOutlined as DocIcon,
  PictureAsPdfOutlined as PdfIcon,
  ImageOutlined as ImageIcon,
  MailOutline as MailIcon,
  AttachEmailOutlined as EmailIcon,
  InsertDriveFileOutlined as GenericFileIcon,
} from "@mui/icons-material";
import { Box, TextField, Typography, Tooltip } from "@mui/material";
import { useSnackbar } from "notistack";
import PropTypes from "prop-types";
import { useRef, useState } from "react";
import DocumentViewer from "../Viewer/DocumentViewer.jsx";
import { v4 as uuidv4 } from "uuid";
import { Button, dimensions, typographyTokens } from "../../../theme/index.js";
import { componentTokens } from "../../../theme/tokens/components.js";
import { backendGet } from "../../../services/dataService.js";

DnDFileUpload.propTypes = {
  upload: PropTypes.array,
  setUpload: PropTypes.func,
  allowedExtensions: PropTypes.array,
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
  add_description: PropTypes.bool,
};

export default function DnDFileUpload(props) {
  const {
    upload = {},
    setUpload,
    allowedExtensions = [],
    disabled = false,
    multiple = false,
    add_description = false,
  } = props;
  const [dragging, setDragging] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef(null);
  const [documentToView, setDocumentToView] = useState(null);
  const itemHeight = add_description ? "56px" : "36px";

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setDragging(false);
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const removeFile = (itemToRemove) => {
    setUpload(upload.filter((item) => item !== itemToRemove));
  };

  const handleFileInputChange = (e) => {
    if (!disabled) handleFiles(Array.from(e.target.files));
  };

  const isExtensionAllowed = (filename) => {
    const extension = filename.split(".").pop().toLowerCase();
    const loweredExtensions = allowedExtensions.map((extension) =>
      extension.toLowerCase()
    );
    return loweredExtensions.includes(extension);
  };

  const handleChangeBezeichnung = (e, item) => {
    setUpload(
      upload.map((singleFile) => {
        if (singleFile.id === item.id) {
          return { ...singleFile, label: e.target.value };
        } else return singleFile;
      })
    );
  };

  const getFileType = (filename) => {
    let filetype = "Download";
    const filenameLower = filename.toLowerCase().trim();
    if (filenameLower.endsWith(".txt")) filetype = "TXT";
    if (filenameLower.endsWith(".pdf")) filetype = "PDF";
    if (filenameLower.endsWith(".msg")) filetype = "MSG";
    if (filenameLower.endsWith(".eml")) filetype = "EML";
    if (filenameLower.endsWith(".png")) filetype = "Image";
    if (filenameLower.endsWith(".jpg")) filetype = "Image";
    if (filenameLower.endsWith(".jpeg")) filetype = "Image";
    if (filenameLower.endsWith(".tif")) filetype = "Image";
    if (filenameLower.endsWith(".tiff")) filetype = "Image";
    if (filenameLower.endsWith(".mp4")) filetype = "Video";
    if (filenameLower.endsWith(".mov")) filetype = "Video";
    if (filenameLower.endsWith(".m4v")) filetype = "Video";
    if (filenameLower.endsWith(".webm")) filetype = "Video";
    if (filenameLower.endsWith(".3gp")) filetype = "Video";
    if (filenameLower.endsWith(".xls")) filetype = "Download";
    if (filenameLower.endsWith(".xlsx")) filetype = "Download";
    return filetype;
  };

  const accept = allowedExtensions?.length
    ? allowedExtensions.map((ext) => "." + String(ext).toLowerCase()).join(",")
    : undefined;

  const handleFiles = async (files) => {
    let newFiles = [...upload];

    const filePromises = files.map((uploadFile) => {
      return new Promise((resolve, reject) => {
        if (isExtensionAllowed(uploadFile.name)) {
          const reader = new FileReader();

          reader.onload = (e) => {
            const fileContent = e.target.result;
            const newFileItem = {
              id: uuidv4(),
              filename: uploadFile.name,
              filesize: uploadFile.size,
              mimetype: uploadFile.type,
              filetype: getFileType(uploadFile.name),
              filedata: fileContent,
              label: add_description ? "" : uploadFile.name,
            };
            if (!multiple) newFiles = [];
            newFiles.push(newFileItem);
            resolve();
          };
          reader.readAsDataURL(uploadFile);
        } else {
          enqueueSnackbar(
            "Ungültige Dateiendung '" +
              uploadFile.name.split(".").pop().toLowerCase() +
              "' - Folgende Dateiendungen sind gültig: " +
              allowedExtensions.join(", "),
            { variant: "error" }
          );
          reject();
        }
      });
    });

    try {
      await Promise.all(filePromises);
      setUpload(newFiles);
    } catch (error) {
      enqueueSnackbar(
        "Fehler bei der Verarbeitung der hochgeladenen Dateien!",
        { variant: "error" }
      );
    }
  };

  const openFileDialog = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const showDocument = async (document) => {
    // For existing files, fetch content on demand
    if (document.id && !document.filedata) {
      try {
        const result = await backendGet(`data/anlages/${document.id}`, {
          options: {
            params: {
              include: ["filedata"],
            },
          },
          label: "Datei laden",
          setIsLoading: () => {},
          setResult: () => {},
          enqueueSnackbar,
        });
        if (result?.filedata) {
          document = { ...document, filedata: result.filedata };
        }
      } catch (err) {
        enqueueSnackbar("Fehler beim Laden der Datei", { variant: "error" });
        return;
      }
    }

    document.success = true;

    /*
    Workaround: Um die selbe Vorschau nach dem schließen wieder öffnen zu können
    wenn man nämlich das selbe Dokument öffnet, wird das document nicht neu gesetzt, deshalb
    wird hier ein Timeout gesetzt, um das documentToView auf null zu setzen und dann sofort wieder
    auf das Dokument zu setzen, damit die Vorschau neu gerendert wird
    */
    setDocumentToView(null);
    setTimeout(() => setDocumentToView(document), 0);
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return "";

    const kb = bytes / 1024;
    const mb = kb / 1024;

    if (mb >= 1) {
      return `${mb.toFixed(1).replace(".", ",")} MB`;
    }

    return `${Math.round(kb).toLocaleString("de-DE")} KB`;
  };

  const getFileIcon = (filetype) => {
    console.log(filetype);
    switch (filetype) {
      case "PDF":
        return <PdfIcon fontSize="small" />;
      case "Image":
        return <ImageIcon fontSize="small" />;
      case "DOC":
        return <DocIcon fontSize="small" />;
      case "MSG":
        return <MailIcon fontSize="small" />;
      case "EML":
        return <EmailIcon fontSize="small" />;
      default:
        return <GenericFileIcon fontSize="small" />;
    }
  };

  return (
    <>
      <Box
        sx={{
          border: `1px dashed ${dragging ? componentTokens.uploadDropzone.border.drag : componentTokens.uploadDropzone.border.default}`,
          borderRadius: componentTokens.uploadDropzone.borderRadius,
          padding: componentTokens.uploadDropzone.padding,
          textAlign: "center",
          minHeight: "100px",
          ":hover": {
            cursor: disabled ? "default" : "pointer",
            backgroundColor: disabled ? "lightgray" : "clickableHover",
          },
          backgroundColor: disabled
            ? componentTokens.uploadDropzone.background.disabled
            : dragging
              ? componentTokens.uploadDropzone.background.dragging
              : componentTokens.uploadDropzone.background.default,
          opacity: disabled ? 0.6 : 1,
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <Box>
          <Box sx={{ mb: dimensions["md"] }}>
            <UploadIcon />
          </Box>
          <Box sx={{ mb: dimensions["md"] }}>
            Datei hier ablegen
            <br />
            {dragging ? "... " : " oder "}
          </Box>
          <Button
            color={"primary"}
            onClick={openFileDialog}
            sx={{
              opacity: dragging ? 0.1 : 1,
              mb: dimensions["lg"],
            }}
          >
            Auswählen
          </Button>

          <Typography
            component="div"
            sx={{
              fontSize: typographyTokens.size.sm,
              fontWeight: typographyTokens.weight.regular,
              color: componentTokens.uploadDropzone.text.color,
              maxWidth: "75%",
              mx: "auto",
              textWrap: "balance",
              mb: dimensions["md"],
            }}
          >
            <p>Maximale Dateigröße: 15 MB</p>
            <p>Zulässige Dateitypen: {allowedExtensions.join(", ")}</p>
          </Typography>
        </Box>

        <input
          type="file"
          multiple={multiple}
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileInputChange}
          accept={accept}
        />
      </Box>
      <Box sx={{ mt: "20px", pl: "0px", pr: "0px" }}>
        {upload.map((item, index) => (
          <Box
            key={index}
            sx={{
              mt: "5px",
              paddingRight: componentTokens.listItem.paddingRight,
              paddingTop: componentTokens.listItem.paddingY,
              paddingBottom: componentTokens.listItem.paddingY,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                height: itemHeight,
                textAlign: "left",
                mb: add_description ? dimensions["lg"] : 0,
              }}
            >
              {add_description && (
                <TextField
                  sx={{
                    mt: "0px",
                    mb: dimensions["md"],
                    pr: dimensions["lg"],
                  }}
                  size="small"
                  fullWidth
                  value={item.label}
                  onChange={(e) => handleChangeBezeichnung(e, item)}
                  label="Bezeichnung"
                  variant="outlined"
                  slotProps={{
                    input: { sx: { fontSize: "12px", height: "35px" } },

                    inputLabel: {
                      sx: {
                        fontSize: typographyTokens.size.sm,
                      },
                    },
                  }}
                />
              )}

              <Typography
                sx={{
                  fontSize: typographyTokens.size.sm,
                  display: "flex",
                  alignItems: "center",
                  gap: dimensions["2xl"],
                  minWidth: 0,
                }}
              >
                {getFileIcon(item.filetype)}
                {formatFileSize(item.filesize)}
                <Tooltip title={item.filename}>
                  <Box
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {item.filename}
                  </Box>
                </Tooltip>
              </Typography>
            </Box>

            <Box sx={{ display: "flex" }}>
              <Button
                elevation={0}
                sx={{
                  height: componentTokens.listItem.actionButton.size,
                  minWidth: componentTokens.listItem.actionButton.size,
                  width: componentTokens.listItem.actionButton.size,
                  backgroundColor: "transparent",
                  // hover
                  "&:hover": {
                    backgroundColor:
                      componentTokens.listItem.actionButton.remove
                        .hoverBackground,
                    boxShadow: "none",
                  },
                }}
                disabled={disabled}
                onClick={() => removeFile(item)}
              >
                <DeleteIcon
                  sx={{
                    color:
                      componentTokens.listItem.actionButton.remove.iconColor,
                  }}
                />
              </Button>
              &nbsp;
              {item?.filetype !== "MSG" && (
                <Button
                  sx={{
                    height: componentTokens.listItem.actionButton.size,
                    minWidth: componentTokens.listItem.actionButton.size,
                    width: componentTokens.listItem.actionButton.size,
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor:
                        componentTokens.listItem.actionButton.preview
                          .hoverBackground,
                      boxShadow: "none",
                    },
                  }}
                  disabled={disabled}
                  onClick={(e) => showDocument(item)}
                >
                  <PreviewIcon
                    sx={{
                      color:
                        componentTokens.listItem.actionButton.preview.iconColor,
                    }}
                  />
                </Button>
              )}
            </Box>
          </Box>
        ))}
        <DocumentViewer key={documentToView?.id} document={documentToView} />
      </Box>
    </>
  );
}
